import { useIntl } from "react-intl";
import { getConfig, mcpUrl } from "@/lib/config";
import { CopyField } from "./ClaudeConnectPanel";

// Connection card for the MCP Apps (@external-adr AGENT-SERVICE-0024-mcp-apps-per-app-connectors): interactive widgets
// served at `${mcpUrl()}/apps/<name>` (e.g. person-search), rendered by Claude
// inside the conversation. ONE card for all apps – each app is its own connector
// (same PUBLIC client id, same login, same permissions), but the steps are
// identical, so they are stated once and the apps are listed as URL rows.
// Rendered only when config.mcp.enabled and config.mcp.apps is non-empty.
//
// TWO instruction sections, both always shown – which one applies depends on
// where the MCP endpoint lives, and the reader knows that better than we do:
//  - INTERNAL (intranet environments like trunk): claude.ai and the standard
//    Claude apps cannot connect – they broker custom connectors through
//    Anthropic's cloud – so the steps target Claude Desktop in third-party
//    inference mode on the VPN, whose managed connectors dial from the user's
//    own device (@external-adr AGENT-SERVICE-0025-mcp-facade-challenges-initialize: the probe behavior that shaped the
//    facade's auth posture, and the exact OAuth pairing – BYO client
//    `mcpClient`, callback localhost:45454, the port @external-adr AGENT-SERVICE-0021-public-oauth-client-for-claude-integration already
//    whitelisted for Claude Code).
//  - EXTERNAL (internet-reachable deployments): the standard claude.ai
//    custom-connector steps.

// person-search → Person Search
function humanize(name: string): string {
  return name
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function McpAppConnectPanel({ apps }: { apps: string[] }) {
  const intl = useIntl();
  const clientId = getConfig().mcp.clientId;

  return (
    <div className="box bg-surface p-6">
      <div className="text-smycles-micro tracking-[0.25em] text-ink-muted">
        {intl.formatMessage({ id: "userSettings.claude.eyebrow", defaultMessage: "Claude" })}
      </div>
      <h2 className="mt-1 font-serif text-smycles-display-s leading-tight">
        {intl.formatMessage({ id: "userSettings.mcpApp.title", defaultMessage: "Apps in Claude" })}
      </h2>
      <p className="mt-2 max-w-xl text-smycles-body text-ink">
        {intl.formatMessage({
          id: "userSettings.mcpApp.intro",
          defaultMessage:
            "Interactive Smycles apps rendered directly inside your Claude conversations. You sign in with your normal Smycles account, and each app sees exactly what you can see.",
        })}
      </p>

      {/* The app connector URLs: one row per app. */}
      <div className="mt-6 space-y-4">
        <div className="text-smycles-micro tracking-[0.25em] text-ink-muted">
          {intl.formatMessage({ id: "userSettings.mcpApp.appsTitle", defaultMessage: "Apps" })}
        </div>
        {apps.map((app) => (
          <CopyField key={app} label={humanize(app)} value={`${mcpUrl()}/apps/${app}`} />
        ))}
      </div>

      {/* Sign-in is deliberately its OWN section: the client id belongs to the OAuth flow
          shared by ALL apps, not to any single app row above. */}
      <div className="mt-8 space-y-3 border-t border-ink/10 pt-6">
        <div className="text-smycles-micro tracking-[0.25em] text-ink-muted">
          {intl.formatMessage({
            id: "userSettings.mcpApp.oauthTitle",
            defaultMessage: "Sign-in (OAuth)",
          })}
        </div>
        <p className="max-w-xl text-smycles-meta text-ink-muted">
          {intl.formatMessage({
            id: "userSettings.mcpApp.oauthDesc",
            defaultMessage:
              "Connectors authenticate with OAuth: when you connect, Claude opens the Smycles sign-in, and the app then acts with exactly your permissions. All apps share the public client below.",
          })}
        </p>
        <CopyField
          label={intl.formatMessage({
            id: "userSettings.claude.clientIdLabel",
            defaultMessage: "Client ID",
          })}
          value={clientId}
        />
        <p className="text-smycles-label text-ink-muted">
          {intl.formatMessage({
            id: "userSettings.claude.noSecret",
            defaultMessage: "No client secret is needed. Leave the secret field empty.",
          })}
        </p>
      </div>

      {/* INTERNAL: intranet environments (trunk & co.) – Claude Desktop 3P on the VPN. */}
      <div className="mt-8 space-y-3">
        <div className="text-smycles-micro tracking-[0.25em] text-ink-muted">
          {intl.formatMessage({
            id: "userSettings.mcpApp.internalTitle",
            defaultMessage: "Internal environments – Claude Desktop on the VPN",
          })}
        </div>
        <p className="max-w-xl text-smycles-meta text-ink-muted">
          {intl.formatMessage({
            id: "userSettings.mcpApp.internalDesc",
            defaultMessage:
              "On intranet environments (e.g. trunk), claude.ai and the standard Claude apps cannot connect: they dial from Anthropic's cloud, not from your device. Use Claude Desktop in third-party inference mode on the VPN instead. Every app is its own connector; use its URL from the list above.",
          })}
        </p>
        <ol className="max-w-xl list-decimal space-y-1.5 pl-5 text-smycles-meta text-ink">
          <li>
            {intl.formatMessage({
              id: "userSettings.mcpApp.desktopStep1",
              defaultMessage:
                "Connect to the company VPN. In Claude Desktop, enable Help → Troubleshooting → Enable Developer Mode, then open Developer → Configure Third-Party Inference.",
            })}
          </li>
          <li>
            {intl.formatMessage({
              id: "userSettings.mcpApp.desktopStep2",
              defaultMessage:
                "In the Connection section, choose Anthropic API as the inference provider and enter your Anthropic API key. Skip this if Claude Desktop already runs in third-party mode.",
            })}
          </li>
          <li>
            {intl.formatMessage({
              id: "userSettings.mcpApp.desktopStep3",
              defaultMessage:
                "Under Connectors, add a server: transport Streamable HTTP, URL from the list above.",
            })}
          </li>
          <li>
            {intl.formatMessage({
              id: "userSettings.mcpApp.desktopStep4",
              defaultMessage:
                "Under OAuth choose Bring your own client and enter the Client ID from above plus the callback host and port below. Leave the client secret empty.",
            })}
            {/* The two OAuth values Desktop must be pinned to — copy rows, same pattern as the
                Client ID. They exist because the redirect http://localhost:45454/* is what the
                Keycloak client whitelists (@external-adr AGENT-SERVICE-0025-mcp-facade-challenges-initialize); Desktop's defaults
                (127.0.0.1, random port) fail the sign-in. */}
            <div className="mt-2 space-y-3">
              <CopyField
                label={intl.formatMessage({
                  id: "userSettings.mcpApp.callbackHostLabel",
                  defaultMessage: "Callback host",
                })}
                value="localhost"
              />
              <CopyField
                label={intl.formatMessage({
                  id: "userSettings.mcpApp.callbackPortLabel",
                  defaultMessage: "Callback port",
                })}
                value="45454"
              />
            </div>
          </li>
          <li>
            {intl.formatMessage({
              id: "userSettings.mcpApp.desktopStep5",
              defaultMessage:
                "Apply the changes and restart Claude Desktop. Then open Settings → Connectors, Connect, and sign in with your Smycles account in the browser window.",
            })}
          </li>
        </ol>
      </div>

      {/* EXTERNAL: internet-reachable deployments – the standard claude.ai custom-connector flow. */}
      <div className="mt-8 space-y-3">
        <div className="text-smycles-micro tracking-[0.25em] text-ink-muted">
          {intl.formatMessage({
            id: "userSettings.mcpApp.externalTitle",
            defaultMessage: "External environments – Claude app (desktop, web and mobile)",
          })}
        </div>
        <p className="max-w-xl text-smycles-meta text-ink-muted">
          {intl.formatMessage({
            id: "userSettings.mcpApp.externalDesc",
            defaultMessage:
              "When this environment is reachable from the internet, every app is added in Claude as its own custom connector, separate from the main Smycles connector, using its URL from the list above.",
          })}
        </p>
        <ol className="max-w-xl list-decimal space-y-1.5 pl-5 text-smycles-meta text-ink">
          <li>
            {intl.formatMessage({
              id: "userSettings.claude.appStep1",
              defaultMessage: "In Claude, open Settings, then Connectors.",
            })}
          </li>
          <li>
            {intl.formatMessage({
              id: "userSettings.claude.appStep2",
              defaultMessage: "Choose Add custom connector and paste the Server URL above.",
            })}
          </li>
          <li>
            {intl.formatMessage({
              id: "userSettings.claude.appStep3",
              defaultMessage:
                "Open Advanced settings, enter the Client ID above, and leave the client secret empty.",
            })}
          </li>
          <li>
            {intl.formatMessage({
              id: "userSettings.claude.appStep4",
              defaultMessage:
                "Add the connector, then Connect and sign in with your Smycles account.",
            })}
          </li>
        </ol>
      </div>
      {/* No Claude Code section here on purpose: developers get the same search tools through
          the MAIN Smycles connector (panel above) – the app connectors only add the widgets,
          which Claude Code doesn't render. */}
    </div>
  );
}
